import { FC } from "react";
import { Stack, StackProps, MenuItem } from "@mui/material";
import { Field as FormikField } from "formik";
import { TextField as FmuiTextField } from "formik-mui";
import { FrequencyOptions } from "./models";
import StackedStaticDataDisplay from "common/StackedStaticDataDisplay";
import { DateTime } from "luxon";
import { formatDate } from "util/formats";
import { makeStyles } from "makeStyles";
import { NumberedTextFieldEndAdornment } from "common/NumberedTextfield";
import PrsDatePickerField from "common/FormikFields/PrsDatePickerField";

const useStyles = makeStyles()({
  stackItemWidth: {
    width: "25%",
    margin: 0
  },
  dateWidth: {
    width: "16em"
  }
});

export interface MonitoringHeaderStackProps extends Omit<StackProps, "onChange"> {
  monitoringFrequencyInDays: number | null;
  monitoringOccurrencesRemaining: number | null;
  currentMonitoringPeriodDate?: string;
  monitoringCompleted: boolean;
  monitoringCompletedOnDate?: string | null;
  createdDate: string | null;
  monitoringSingleHardDeadlineDate?: DateTime | null;
  isSubmitting?: boolean;
  lastCompletedMonitoringDate?: string | null;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
}

const MAX_FREQUENCY_INPUT = 10;

export const MonitoringHeaderStack: FC<MonitoringHeaderStackProps> = ({
  monitoringFrequencyInDays,
  monitoringOccurrencesRemaining,
  currentMonitoringPeriodDate,
  monitoringCompleted,
  monitoringCompletedOnDate,
  createdDate,
  monitoringSingleHardDeadlineDate,
  isSubmitting = false,
  lastCompletedMonitoringDate,
  setFieldValue,
  ...stackProps
}) => {
  const { classes } = useStyles();

  const isMonitoringDeadline = monitoringFrequencyInDays === FrequencyOptions.HardDeadline.value;
  const disableMonitoringSingleHardDeadlineDate = (isMonitoringDeadline && lastCompletedMonitoringDate) || isSubmitting;
  const endDate = monitoringSingleHardDeadlineDate
    ? monitoringSingleHardDeadlineDate
    : currentMonitoringPeriodDate && monitoringFrequencyInDays !== null && monitoringOccurrencesRemaining !== null
    ? DateTime.fromISO(currentMonitoringPeriodDate).plus({ days: monitoringFrequencyInDays * monitoringOccurrencesRemaining })
    : null;

  return (
    <Stack {...stackProps}>
      <StackedStaticDataDisplay label="Monitoring Started On" value={formatDate(createdDate)} className={classes.stackItemWidth} />
      {monitoringCompleted ? (
        <>
          <StackedStaticDataDisplay label="Monitoring Completed On" value={formatDate(monitoringCompletedOnDate ?? null)} />
        </>
      ) : (
        <>
          <FormikField
            component={FmuiTextField}
            name="monitoringFrequencyInDays"
            label="Frequency"
            select
            disabled
            className={classes.stackItemWidth}>
            {Object.entries(FrequencyOptions).map(([key, { value, label }]) => (
              <MenuItem key={key} value={value}>
                {label}
              </MenuItem>
            ))}
          </FormikField>

          {isMonitoringDeadline ? (
            <FormikField
              component={PrsDatePickerField}
              name="monitoringSingleHardDeadlineDate"
              label="Deadline Date"
              className={classes.stackItemWidth}
              minDate={DateTime.local()}
              disabled={disableMonitoringSingleHardDeadlineDate}
            />
          ) : (
            <FormikField
              component={FmuiTextField}
              name="monitoringOccurrencesRemaining"
              label="Occurrences Remaining"
              disabled
              InputProps={{
                endAdornment: (
                  <NumberedTextFieldEndAdornment
                    value={monitoringOccurrencesRemaining ?? 0}
                    onChange={(newValue) => setFieldValue("monitoringOccurrencesRemaining", newValue)}
                    min={0}
                    max={MAX_FREQUENCY_INPUT}
                  />
                )
              }}
              className={classes.stackItemWidth}
              InputLabelProps={{ shrink: true }}
            />
          )}
          <StackedStaticDataDisplay
            label="Tentative Monitoring End Date"
            value={formatDate(endDate?.toISO() ?? null)}
            className={classes.stackItemWidth}
          />
        </>
      )}
    </Stack>
  );
};

export default MonitoringHeaderStack;
